/**
 * A faixa da rescisão do contrato, no topo de todas as páginas do painel (o espaço `avisos` do
 * `EstruturaPainel`, logo abaixo do cabeçalho).
 *
 * - **Programada** (`tipo: "programada"`): o texto pré-feito («O contrato da … termina: o painel
 *   fecha a dd/mm/aaaa. Descarrega o que precisares até lá.») e, por baixo, a nota que a Honga
 *   Yetu escreveu no Filament. Não se fecha: está lá até ao dia do fecho.
 * - **Fechada** (`tipo: "fechada"`): só a equipa da Honga Yetu chega ao painel depois do fecho, e
 *   vê esta faixa a lembrá-lo.
 *
 * Os dados vêm do servidor (`aviso_rescisao_escola` no painel da Escola; no Logigate também
 * `aviso_rescisao_empresa`) — ver `Modules\Escola\Services\Rescisao\Rescisoes`. Sem animações.
 *
 * A eliminação agendada da escola usa a mesma faixa, no aspecto «programada»
 * (`aviso_eliminacao_escola` e `aviso_eliminacao_empresa`, do
 * `Modules\Escola\Services\Eliminacao\EliminacoesAgendadas`).
 */
import { CalendarClock, Lock } from "lucide-react";
import { cn } from "@/lib/utils";

export interface DadosAvisoRescisao {
    tipo: "programada" | "fechada";
    titulo: string;
    texto: string;
    nota?: string | null;
    data_fecho: string;
}

export default function AvisoRescisao({ aviso }: { aviso?: DadosAvisoRescisao | null }) {
    if (!aviso?.texto) {
        return null;
    }

    const fechada = aviso.tipo === "fechada";
    const aspecto = fechada
        ? {
              faixa: "border-red-200 bg-red-50 text-red-950 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-50",
              chip: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
              nota: "border-red-200/80 dark:border-red-500/20",
          }
        : {
              faixa: "border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-50",
              chip: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
              nota: "border-amber-200/80 dark:border-amber-500/20",
          };

    return (
        <div role="status" data-aviso-rescisao={aviso.tipo} className={cn("border-b", aspecto.faixa)}>
            <div className="mx-auto flex w-full max-w-[1600px] items-start gap-3 px-3 py-3 sm:px-6 lg:px-8">
                <span className={cn("flex size-8 shrink-0 items-center justify-center rounded-lg", aspecto.chip)} aria-hidden>
                    {fechada ? <Lock className="size-[18px]" /> : <CalendarClock className="size-[18px]" />}
                </span>
                <div className="min-w-0 flex-1 text-[13px] leading-snug">
                    <p className="font-semibold">{aviso.titulo}</p>
                    <p className="mt-0.5 opacity-90">{aviso.texto}</p>
                    {aviso.nota && (
                        <p className={cn("mt-2 whitespace-pre-line border-l-2 pl-3 text-xs opacity-80", aspecto.nota)}>{aviso.nota}</p>
                    )}
                </div>
            </div>
        </div>
    );
}
